import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { restoreParsingCase } from './restoreParsingCase';
import { type RestoreParsingCaseResponse } from './types';

/**
 * React Query mutation hook for restoring a DISMISSED or ARCHIVED parsing case.
 * Clears dismiss/archive metadata and triggers a fresh parse run, ending in IN_PROGRESS.
 *
 * Pass the `caseId` as an argument to the returned `mutate` function.
 *
 * @param options - Additional React Query mutation options (onSettled is reserved)
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Parsing-Cases/operation/post_restore_parsing_case_delivery_note_parsing_cases__case_id__actions_restore_post
 */
export const useMutationRestoreParsingCase = (
  options?: Omit<
    UseMutationOptions<RestoreParsingCaseResponse, Error, UUID>,
    'mutationFn' | 'onSettled'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<RestoreParsingCaseResponse, Error, UUID>({
    mutationFn: (caseId) => restoreParsingCase(caseId),
    onSettled(_data, _error, caseId) {
      void queryClient.invalidateQueries({
        queryKey: [queryKeysDeliveryNote.base()],
      });
      queryClient.removeQueries({
        queryKey: queryKeysDeliveryNote.getParsingCase(caseId),
      });
    },
    ...options,
  });
};
